import { PriceType } from '@cast/shared-types';
import { getPriceMultiplier } from '@cast/utils';

import { useCostOverTime } from 'features/cost-report/hooks/useCostOverTime';
import { useCostOverTimeSection } from 'features/cost-report/hooks/useCostOverTimeSection';
import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import {
  CostReportChartDataByPeriod,
  CostReportChartType,
  NormalizedCostChartData,
  NormalizedCostMode,
} from 'features/cost-report/types/costOverTime';
import { getDataHoursMultiplier } from 'features/cost-report/utils/costOverTime';

type ExtractedDataPoint = {
  normalizedCost: number | null;
  onDemandCost: number | null;
  spotCost: number | null;
  fallbackCost: number | null;
};

export type DataPointMapper = (
  datapoint: CostReportChartDataByPeriod
) => ExtractedDataPoint;

type MapToSelectedRateCostParams = {
  chartType: CostReportChartType;
  costRate: PriceType;
  mapper: DataPointMapper;
  timezone?: string;
};

const mapToSelectedRateCost = ({
  chartType,
  costRate,
  mapper,
  timezone,
}: MapToSelectedRateCostParams) => {
  return (datapoint: CostReportChartDataByPeriod): NormalizedCostChartData => {
    const multiplier =
      chartType === CostReportChartType.BAR
        ? getDataHoursMultiplier(datapoint, costRate, timezone)
        : getPriceMultiplier(costRate);

    const { timestamp, forecast: isForecast } = datapoint;

    const { normalizedCost, onDemandCost, spotCost, fallbackCost } =
      mapper(datapoint);

    const remainingHours =
      costRate === PriceType.DAILY ? 24 - multiplier : null;

    const forecastFields: Record<string, number | null> = {
      normalizedForecast: null,
      onDemandForecast: null,
      fallbackForecast: null,
      spotForecast: null,
      normalizedForecastDelta: null,
      onDemandForecastDelta: null,
      fallbackForecastDelta: null,
      spotForecastDelta: null,
    };

    if (costRate === PriceType.DAILY) {
      if (normalizedCost !== null) {
        forecastFields.normalizedForecast = normalizedCost * 24;
        forecastFields.normalizedForecastDelta =
          remainingHours && remainingHours > 0
            ? normalizedCost * remainingHours
            : null;
      }

      if (onDemandCost !== null) {
        forecastFields.onDemandForecast = onDemandCost * 24;
        forecastFields.onDemandForecastDelta =
          remainingHours && remainingHours > 0
            ? onDemandCost * remainingHours
            : null;
      }

      if (fallbackCost !== null) {
        forecastFields.fallbackForecast = fallbackCost * 24;
        forecastFields.fallbackForecastDelta =
          remainingHours && remainingHours > 0
            ? fallbackCost * remainingHours
            : null;
      }

      if (spotCost !== null) {
        forecastFields.spotForecast = spotCost * 24;
        forecastFields.spotForecastDelta =
          remainingHours && remainingHours > 0
            ? spotCost * remainingHours
            : null;
      }
    }

    let normalized: number | null = null;
    let onDemand: number | null = null;
    let fallback: number | null = null;
    let spot: number | null = null;

    if (normalizedCost !== null) {
      normalized = normalizedCost * multiplier;
    }

    if (onDemandCost !== null) {
      onDemand = onDemandCost * multiplier;
    }

    if (fallbackCost !== null) {
      fallback = !isForecast ? fallbackCost * multiplier : 0;
    }

    if (spotCost !== null) {
      spot = spotCost * multiplier;
    }

    return {
      timestamp,
      [NormalizedCostMode.NORMALIZED]: normalized,
      [NormalizedCostMode.ON_DEMAND]: onDemand,
      [NormalizedCostMode.FALLBACK]: fallback,
      [NormalizedCostMode.SPOT]: spot,
      forecast: isForecast,
      ...forecastFields,
    };
  };
};

const mapToUptimeCost =
  (costRate: PriceType, timeSeriesStep: PriceType, mapper: DataPointMapper) =>
  (datapoint: CostReportChartDataByPeriod): NormalizedCostChartData => {
    const multiplier = getPriceMultiplier(costRate, timeSeriesStep);

    const { timestamp, forecast: isForecast } = datapoint;

    const { normalizedCost, onDemandCost, spotCost, fallbackCost } =
      mapper(datapoint);

    let normalized: number | null = null;
    let onDemand: number | null = null;
    let fallback: number | null = null;
    let spot: number | null = null;

    if (normalizedCost !== null) {
      normalized = normalizedCost * multiplier;
    }

    if (onDemandCost !== null) {
      onDemand = onDemandCost * multiplier;
    }

    if (fallbackCost !== null) {
      fallback = !isForecast ? fallbackCost * multiplier : 0;
    }

    if (spotCost !== null) {
      spot = spotCost * multiplier;
    }

    return {
      timestamp,
      [NormalizedCostMode.NORMALIZED]: normalized,
      [NormalizedCostMode.ON_DEMAND]: onDemand,
      [NormalizedCostMode.FALLBACK]: fallback,
      [NormalizedCostMode.SPOT]: spot,
      forecast: isForecast,
    };
  };

export const useNormalizedResourceCostChartData = (
  mapper: (datapoint: CostReportChartDataByPeriod) => ExtractedDataPoint
) => {
  const { chartType, timezone } = useCostReportContext();
  const { current, projectEndOfDayCost, timeSeriesStep } = useCostOverTime();
  const { costRate } = useCostOverTimeSection();

  const data = current.chartData.map(
    projectEndOfDayCost
      ? mapToSelectedRateCost({ chartType, costRate, mapper, timezone })
      : mapToUptimeCost(costRate, timeSeriesStep, mapper)
  );

  return { isLoading: current.isLoading, data };
};
